import { supabase } from '../lib/supabase';

export interface UserProfile {
  id: string;
  email: string;
  stripe_customer_id: string | null;
  subscription_status: 'rookie' | 'pro' | 'trialing' | 'elite' | 'cancelled' | 'past_due';
  subscription_id: string | null;
  current_period_start: string | null;
  current_period_end: string | null;
  cancel_at_period_end: boolean;
  practice_generation_count: number;
  game_plan_generation_count: number;
  ai_chat_message_count?: number;
  tennis_match_scoring_count?: number;
  usage_period_start?: string | null;
  created_at: string;
  updated_at: string;
}

/** Monthly usage counters tracked on `profiles`, matching the `increment_usage_counter` RPC's allowlist. */
export type UsageCounterKey =
  | 'practice_generation_count'
  | 'game_plan_generation_count'
  | 'ai_chat_message_count'
  | 'tennis_match_scoring_count';

/** Rookie monthly limits (reset each calendar month). Pro has no cap. */
export const ROOKIE_LIMITS = {
  CHAT_MESSAGES: 20,
  PRACTICE_PLANS: 3,
  GAME_PLANS: 1,
  TENNIS_MATCH_SCORING: 1,
} as const;

export interface SubscriptionPlans {
  rookie: {
    name: string;
    price: number;
    priceId: string;
    features: string[];
  };
  pro: {
    name: string;
    monthly: {
      price: number;
    };
    annual: {
      price: number;
      monthlyEquivalent: number;
      savings: number;
    };
    features: string[];
  };
  elite: {
    name: string;
    price: number;
    priceId: string;
    features: string[];
  };
}

export const SUBSCRIPTION_PLANS: SubscriptionPlans = {
  rookie: {
    name: 'Rookie',
    price: 0,
    priceId: '',
    features: [
      `${ROOKIE_LIMITS.CHAT_MESSAGES} AI Coach messages/month`,
      `${ROOKIE_LIMITS.PRACTICE_PLANS} AI-powered practice plans/month`,
      `${ROOKIE_LIMITS.GAME_PLANS} AI-powered game plan/month`,
      `${ROOKIE_LIMITS.TENNIS_MATCH_SCORING} live-scored Tennis match/month`,
      'Up to 1 team',
      'Lineup & depth charts',
      'Stat tracking',
      'Unlimited manual practice/drill creation',
      'Roster management',
      'Player Development'
    ],
  },
  pro: {
    name: 'Pro',
    monthly: {
      price: 12.99,
    },
    annual: {
      price: 89.99,
      monthlyEquivalent: 7.50,
      savings: 42,
    },
    features: [
      'Everything in Rookie, plus...',
      'Unlimited AI Coach messages',
      'Unlimited AI-powered practice planning',
      'Unlimited AI-powered game planning',
      'Multiple teams',
      'Lineup & depth charts with AI optimization',
      'Stat tracking w/ GameChanger import',
      'Advanced features (Practice duplication)',
      'Priority support'
    ],
  },
  elite: {
    name: 'Elite',
    price: 0,
    priceId: '',
    features: [
      'All Pro features',
      'Live game tracking',
      'Advanced statistics',
      'Real-time game management'
    ],
  },
};

class SubscriptionService {
  /** Reset monthly usage counts when the calendar month changes. Call before reading/incrementing counts. */
  async ensureUsagePeriodResetted(): Promise<void> {
    const { error } = await supabase.rpc('ensure_usage_period_reset');
    if (error) {
      console.error('Error resetting usage period:', error);
    }
  }

  async getUserProfile(userId: string): Promise<UserProfile | null> {
    await this.ensureUsagePeriodResetted();
    const { data, error } = await supabase
      .from('profiles')
      .select('*')
      .eq('id', userId)
      .single();

    // If profile doesn't exist, create one for existing users
    if (error && error.code === 'PGRST116') { // Not found error
      console.log('Profile not found, creating one for existing user...');
      
      // Get user email from auth
      const { data: userData } = await supabase.auth.getUser();
      if (!userData.user?.email) {
        console.error('Cannot create profile: user email not found');
        return null;
      }

      // Create profile for existing user
      const { data: newProfile, error: createError } = await supabase
        .from('profiles')
        .insert({
          id: userId,
          email: userData.user.email,
          subscription_status: 'rookie'
        })
        .select()
        .single();

      if (createError) {
        console.error('Error creating user profile:', createError);
        return null;
      }

      return newProfile;
    }

    if (error) {
      console.error('Error fetching user profile:', error);
      return null;
    }

    return data;
  }

  async createCheckoutSession(userId: string, interval: 'monthly' | 'annual' = 'monthly'): Promise<string | null> {
    try {
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;

      // SECURITY: Get user's session token instead of anon key
      const { data: { session } } = await supabase.auth.getSession();

      if (!session?.access_token) {
        throw new Error('User must be authenticated to create checkout session');
      }

      const response = await fetch(`${supabaseUrl}/functions/v1/stripe-checkout`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          // SECURITY: Use user's session token, not anon key
          'Authorization': `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({
          userId,
          interval,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to create checkout session');
      }

      const { url } = await response.json();
      return url;
    } catch (error) {
      console.error('Error creating checkout session:', error);
      return null;
    }
  }

  async createPortalSession(userId: string): Promise<string | null> {
    try {
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      
      // SECURITY: Get user's session token instead of anon key
      const { data: { session } } = await supabase.auth.getSession();
      
      if (!session?.access_token) {
        throw new Error('User must be authenticated to access billing portal');
      }
      
      const response = await fetch(`${supabaseUrl}/functions/v1/stripe-portal`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          // SECURITY: Use user's session token, not anon key
          'Authorization': `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({ userId }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to create portal session');
      }

      const { url } = await response.json();
      return url;
    } catch (error) {
      console.error('Error creating portal session:', error);
      return null;
    }
  }

  async subscribeToPro(userId: string, interval: 'monthly' | 'annual' = 'monthly'): Promise<boolean> {
    const url = await this.createCheckoutSession(userId, interval);
    if (url) {
      window.location.href = url;
      return true;
    }
    return false;
  }

  /**
   * After Stripe checkout success, the user is redirected back with ?session_id=...
   * Call this to sync the subscription from Stripe to the profile (in case the webhook
   * hasn't run yet or isn't configured, e.g. local dev).
   */
  async syncSubscriptionFromSession(sessionId: string): Promise<boolean> {
    try {
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) return false;
      const response = await fetch(`${supabaseUrl}/functions/v1/stripe-sync-session`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({ session_id: sessionId }),
      });
      if (!response.ok) return false;
      const data = await response.json();
      return data?.success === true;
    } catch {
      return false;
    }
  }

  async openBillingPortal(userId: string): Promise<boolean> {
    const url = await this.createPortalSession(userId);
    if (url) {
      window.location.href = url;
      return true;
    }
    return false;
  }

  /** Cancel Pro subscription at end of billing period; user reverts to Rookie when period ends. */
  async cancelSubscription(userId: string): Promise<{ success: boolean; error?: string }> {
    try {
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) {
        return { success: false, error: 'User must be authenticated to cancel subscription' };
      }
      const response = await fetch(`${supabaseUrl}/functions/v1/stripe-cancel`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({ userId }),
      });
      const data = await response.json();
      if (!response.ok) {
        return { success: false, error: (data as { error?: string }).error || 'Failed to cancel subscription' };
      }
      return { success: true };
    } catch (error) {
      console.error('Error cancelling subscription:', error);
      return { success: false, error: error instanceof Error ? error.message : 'Failed to cancel subscription' };
    }
  }

  /**
   * Statuses that grant everything Pro grants.
   *
   * `elite` is an unsold placeholder tier (see SUBSCRIPTION_PLANS) that is not
   * reachable through checkout today. It is listed here so that if a row ever
   * carries it, the user is treated as paid rather than silently gated as free —
   * it previously passed isSubscriptionActive while failing isProUser.
   */
  private static readonly PRO_STATUSES = ['pro', 'trialing', 'elite'] as const;

  isProUser(profile: UserProfile | null): boolean {
    if (!profile) return false;
    return (SubscriptionService.PRO_STATUSES as readonly string[]).includes(profile.subscription_status);
  }

  isSubscriptionActive(profile: UserProfile | null): boolean {
    if (!profile) return false;
    return [...SubscriptionService.PRO_STATUSES, 'rookie'].includes(profile.subscription_status);
  }

  getSubscriptionStatusDisplay(status: string): string {
    switch (status) {
      case 'pro':
        return 'Pro (Active)';
      case 'trialing':
        return 'Pro (Trial)';
      case 'rookie':
      case 'cancelled':
        return 'Rookie (Free)';
      case 'elite':
        return 'Elite';
      case 'past_due':
        return 'Past Due';
      default:
        return 'Unknown';
    }
  }

  // Check if user has reached practice generation limit (Rookie: 3/month)
  hasReachedPracticeLimit(profile: UserProfile | null): boolean {
    if (!profile) return false;
    if (this.isProUser(profile)) return false;
    return (profile.practice_generation_count || 0) >= ROOKIE_LIMITS.PRACTICE_PLANS;
  }

  // Check if user has reached game plan generation limit (Rookie: 1/month)
  hasReachedGamePlanLimit(profile: UserProfile | null): boolean {
    if (!profile) return false;
    if (this.isProUser(profile)) return false;
    return (profile.game_plan_generation_count || 0) >= ROOKIE_LIMITS.GAME_PLANS;
  }

  // Check if user has reached AI Coach chat limit (Rookie: 20/month)
  hasReachedChatLimit(profile: UserProfile | null): boolean {
    if (!profile) return false;
    if (this.isProUser(profile)) return false;
    return (profile.ai_chat_message_count ?? 0) >= ROOKIE_LIMITS.CHAT_MESSAGES;
  }

  // Check if user has reached the Tennis live-scoring limit (Rookie: 1 match started/month).
  // Only "Start Match" counts against this — resuming an already-started match never does.
  hasReachedTennisScoringLimit(profile: UserProfile | null): boolean {
    if (!profile) return false;
    if (this.isProUser(profile)) return false;
    return (profile.tennis_match_scoring_count ?? 0) >= ROOKIE_LIMITS.TENNIS_MATCH_SCORING;
  }

  // Get practice generation count (current month)
  getPracticeGenerationCount(profile: UserProfile | null): number {
    if (!profile) return 0;
    return profile.practice_generation_count || 0;
  }

  // Get game plan generation count (current month)
  getGamePlanGenerationCount(profile: UserProfile | null): number {
    if (!profile) return 0;
    return profile.game_plan_generation_count || 0;
  }

  // Get AI chat message count (current month)
  getAiChatMessageCount(profile: UserProfile | null): number {
    if (!profile) return 0;
    return profile.ai_chat_message_count ?? 0;
  }

  /**
   * Increment one monthly usage counter (period reset happens atomically inside the RPC).
   * Single isolated entry point for all AI-generation usage tracking — callers pass the
   * counter they consumed rather than each owning their own increment method, so the type
   * of AI generation used is always explicit at the call site.
   *
   * Throws on failure rather than swallowing it — every call site already wraps this in
   * its own try/catch with call-site-specific logging (a failed count shouldn't block the
   * generation/reply that already succeeded), so swallowing here just meant a lost
   * increment was silently invisible everywhere instead of logged once, precisely.
   */
  async incrementUsageCounter(counter: UsageCounterKey): Promise<void> {
    const { error } = await supabase.rpc('increment_usage_counter', {
      p_counter: counter,
    });
    if (error) {
      throw error;
    }
  }
}

export const subscriptionService = new SubscriptionService(); 